import { useMutation } from '@tanstack/vue-query'
import { defineStore } from 'pinia'
import axios from '@/plugins/axios'
import { computed, ref } from 'vue'
import router from '@/router'
import { useToast } from 'vue-toastification'
import { permissions } from '@/services/ability'
import { persistAuthToken, type AuthTokenType } from '@/services/auth-token'
import type { User } from '@/types/global'

type LoginPayload = {
  email?: string
  password?: string
  challengeToken?: string
  otp?: string
}

type LoginSuccessResponse = {
  token: string
  auth_type?: AuthTokenType
  name: string
  is_admin: boolean
}

type Login2FAChallengeResponse = {
  requires_2fa: true
  challenge_token: string
}

type LoginResponse = LoginSuccessResponse | Login2FAChallengeResponse

const login = async (payload: LoginPayload): Promise<LoginResponse> => {
  if (payload.challengeToken) {
    const response = await axios.post<LoginResponse>('/v1.0/login', {
      challenge_token: payload.challengeToken,
      otp: payload.otp
    })

    return response.data
  }

  const response = await axios.post<LoginResponse>('/v1.0/login', {
    username: payload.email,
    password: payload.password
  })

  return response.data
}

const loginWithIdentityProvider = async (payload: LoginPayload): Promise<LoginResponse> => {
  const response = await axios.post<LoginResponse>('/v1.0/sso/login', {
    username: payload.email,
    password: payload.password
  })

  return response.data
}

type LoginHandlers = {
  onError?: (message: string) => void
  onRequires2FA?: (challengeToken: string) => void
}

async function handleLoginSuccess(
  data: LoginResponse,
  username: string | undefined,
  handlers?: LoginHandlers
) {
  if ('requires_2fa' in data && data.requires_2fa) {
    handlers?.onRequires2FA?.(data.challenge_token)
    return
  }

  persistAuthToken(data.token, data.auth_type ?? 'local')
  localStorage.setItem(
    'user',
    JSON.stringify({
      name: data.name,
      username: username || ''
    })
  )

  sessionStorage.removeItem('otp_phone')
  sessionStorage.removeItem('otp_last_sent')
  sessionStorage.removeItem('otp_sent')
  localStorage.removeItem('selected-session-id')

  await router.push({ name: 'messages' })
  useToast().success('تم تسجيل الدخول بنجاح')
}

function handleLoginError(error: any, handlers?: LoginHandlers) {
  const message = error?.response?.data?.message || error?.response?.data?.error || 'حدث خطأ عند تسجيل الدخول'
  handlers?.onError?.(message)
  useToast().error(message)
}

class AuthStore {
  user = ref<User | null>()

  init = () => {
    this.user.value = localStorage.user ? JSON.parse(localStorage.user) : null
  }

  ability = computed(() => {
    return permissions(this.user.value)
  })

  useLogin = (handlers?: LoginHandlers) =>
    useMutation({
      mutationKey: ['login'],
      retry: 3,
      mutationFn: async (payload: LoginPayload) => {
        try {
          const data = await login(payload)
          return data
        } catch (error) {
          return Promise.reject(error)
        }
      },
      onSuccess: async (data, variables) => {
        await handleLoginSuccess(data, variables.email, handlers)
      },
      onError: async (error: any) => {
        handleLoginError(error, handlers)
      }
    })

  useIdentityProviderLogin = (handlers?: LoginHandlers) =>
    useMutation({
      mutationKey: ['identity-provider-login'],
      mutationFn: async (payload: LoginPayload) => {
        try {
          return await loginWithIdentityProvider(payload)
        } catch (error) {
          return Promise.reject(error)
        }
      },
      onSuccess: async (data, variables) => {
        await handleLoginSuccess(data, variables.email, handlers)
      },
      onError: async (error: any) => {
        handleLoginError(error, handlers)
      }
    })

  useLogout = () =>
    useMutation({
      mutationKey: ['logout'],
      mutationFn: async () => {
        try {
          // const response = await axios.post('/api/web/session/destroy', {
          //   jsonrpc: '2.0',
          //   params: {}
          // })
          // const result = response.data
          // if (result) {
          localStorage.clear()
          router.push({ name: 'home' })
          // }
        } catch (error) {
          return Promise.reject(error)
        }
      },
      onSuccess: async () => {
        const toast = useToast()
        await router.push({ name: 'login' })
        toast.success('تم تسجيل الخروج بنجاح')
      },
      onError: async () => {
        const toast = useToast()
        toast.error('حدث خطأ عند تسجيل الخروج')
      }
    })
}

export const auth = defineStore('auth', () => new AuthStore())
